import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { DollarSign, X } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface DonationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// Initialize Stripe with the provided publishable key (only if key exists)
const stripePromise = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY 
  ? loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY)
  : null;

const DONATION_AMOUNTS = [5, 10, 25, 50, 100];

const DonationModal: React.FC<DonationModalProps> = ({ isOpen, onClose }) => {
  const [selectedAmount, setSelectedAmount] = useState<number>(10);
  const [customAmount, setCustomAmount] = useState<string>('');
  const [isCustom, setIsCustom] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string>('');

  const handleDonation = async () => {
    try {
      setIsProcessing(true);
      setError('');

      // Check if Stripe is configured
      if (!import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY) {
        throw new Error('Stripe is not configured. Please contact support.');
      }

      const amount = isCustom ? parseFloat(customAmount) : selectedAmount;
      
      if (isNaN(amount) || amount <= 0) {
        throw new Error('Please enter a valid donation amount');
      }

      // Add error handling for network connectivity
      const { data, error: functionError } = await supabase.functions.invoke('create-stripe-session', {
        body: { amount: amount * 100 }, // Convert to cents
      }).catch((err) => {
        console.error('Network error:', err);
        throw new Error('Network error: Unable to connect to payment service');
      });

      if (functionError) {
        console.error('Edge function error:', functionError);
        throw new Error(functionError.message || 'Failed to process donation. Please try again later.');
      }

      if (!data?.sessionId) {
        throw new Error('Invalid response from payment service');
      }

      const stripe = await stripePromise;
      if (!stripe) {
        throw new Error('Payment service failed to initialize');
      }

      const { error: redirectError } = await stripe.redirectToCheckout({
        sessionId: data.sessionId
      });

      if (redirectError) {
        console.error('Stripe redirect error:', redirectError);
        throw new Error(redirectError.message || 'Failed to redirect to payment page');
      }

    } catch (err) {
      console.error('Donation error:', err);
      setError(err instanceof Error ? err.message : 'Failed to process donation');
    } finally {
      setIsProcessing(false);
    }
  };

  if (!isOpen) return null;

  // Don't render the donation modal if Stripe is not configured
  if (!import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
        
        <div className="relative bg-[#272727] rounded-xl p-6 max-w-md w-full">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-semibold text-white">Donations Unavailable</h3>
            <button 
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <p className="text-gray-300 mb-6">
            Donation functionality is currently unavailable. Please contact support for alternative donation methods.
          </p>

          <button
            onClick={onClose}
            className="w-full px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      
      <div className="relative bg-[#272727] rounded-xl p-6 max-w-md w-full">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-semibold text-white">Make a Donation</h3>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {error && (
          <div className="mb-6 bg-red-900/50 border border-red-700 text-red-200 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        <div className="mb-6">
          <div className="grid grid-cols-3 gap-3 mb-4">
            {DONATION_AMOUNTS.map((amount) => (
              <button
                key={amount}
                onClick={() => {
                  setSelectedAmount(amount);
                  setIsCustom(false);
                }}
                className={`px-4 py-3 rounded-lg border ${
                  !isCustom && selectedAmount === amount
                    ? 'border-purple-500 bg-purple-500/20 text-white'
                    : 'border-[#404040] text-gray-300 hover:border-[#505050]'
                } transition-colors`}
              >
                ${amount}
              </button>
            ))}
          </div>

          <div className="relative">
            <input
              type="number"
              value={isCustom ? customAmount : ''}
              onChange={(e) => {
                setCustomAmount(e.target.value);
                setIsCustom(true);
              }}
              placeholder="Enter custom amount"
              className={`w-full px-4 py-3 bg-[#272727] text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 border ${
                isCustom 
                  ? 'border-purple-500 bg-purple-500/20' 
                  : 'border-[#404040] hover:border-[#505050]'
              } transition-colors pl-8`}
            />
            <DollarSign className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" />
          </div>
        </div>

        <button
          onClick={handleDonation}
          disabled={isProcessing || (isCustom && !customAmount)}
          className="w-full px-6 py-3 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors disabled:bg-purple-400/50 disabled:cursor-not-allowed flex items-center justify-center"
        >
          {isProcessing ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2" />
              Processing...
            </>
          ) : (
            <>
              <DollarSign className="h-5 w-5 mr-2" />
              Donate ${isCustom ? customAmount || '0' : selectedAmount}
            </>
          )}
        </button>

        <p className="mt-4 text-sm text-gray-400 text-center">
          Secure payments powered by Stripe
        </p>
      </div>
    </div>
  );
};

export default DonationModal;